import React from 'react';
import { useQueryClient } from 'react-query';

import Button from '~/components/button';
import { Field, Form } from '~/components/form';
import InputField from '~/components/inputs/input-field';
import Modal from '~/components/modals/modal';
import SatelliteImage from '~/components/map/satellite-image';
import Tooltip from '~/components/tooltip/tooltip';
import { UpdateLawnError } from '~/components/lawns/new/lawn-size';
import { useLawnMutations } from '~/features/lawns/lawn-actions';
import { captureException } from '~/utils/exception-tracking';
import { number, required, valid } from '~/utils/validation';

import styles from '~/components/modals/lawn-size/lawn-size-modal.module.scss';

const LawnSizeModal = ({ isOpen, onClose, lawn, isDryFert }) => (
  <Modal isOpen={isOpen} onClose={onClose} className={styles.lawnSizeModal}>
    <Content lawn={lawn} isDryFert={isDryFert} onClose={onClose} />
  </Modal>
);

const Content = ({ lawn, isDryFert, onClose }) => {
  const { updateLawn } = useLawnMutations();
  const queryClient = useQueryClient();

  const handleSubmit = (lawnSize) => {
    return updateLawn(lawn.uuid, { size: { sqFt: lawnSize } })
      .then(async () => {
        await queryClient.refetchQueries(['bundles', lawn.uuid]);
        onClose();
      })
      .catch((err) => {
        captureException(new UpdateLawnError('Update Lawn Size Error'), {
          extras: {
            error: JSON.stringify(err),
          },
        });
        return err;
      });
  };

  const imgSrc = isDryFert
    ? `/images/lawn-size-modal/granular-mobile.png`
    : `/images/lawn-size-modal/fert-liquid-mobile.png`;
  const imgSrcSet = isDryFert
    ? `/images/lawn-size-modal/granular-mobile.png, /images/lawn-size-modal/granular-mobile@2x.png 2x`
    : `/images/lawn-size-modal/fert-liquid-mobile.png, /images/lawn-size-modal/fert-liquid-mobile@2x.png 2x`;

  return (
    <div>
      <div className={styles.lawnSizeHeader}>
        <SatelliteImage
          className={styles.lawnSizeImage}
          lat={lawn?.address.latitude}
          lng={lawn?.address.longitude}
          width="94"
          height="89"
        />
        <h2 className={styles.lawnSizeHeaderText}>
          Let's edit your lawn size!
        </h2>
      </div>
      <Form
        className={styles.lawnSizeForm}
        initialValues={{
          lawnSize: lawn?.size.sqFt,
        }}
        onSubmit={(formValues) => handleSubmit(parseInt(formValues.lawnSize))}
        validate={(formValues) => {
          return {
            lawnSize: valid(
              required(' '), // Intentional space so that we DON'T use the default error message (the red label is enough for this use case)
              number('Please enter a number (without any commas, please)')
            )(formValues.lawnSize),
          };
        }}
      >
        {({ isSubmitting }) => (
          <>
            <Field
              label={
                <span className={styles.label}>
                  Area of lawn to treat (sq. ft)
                </span>
              }
              placeholder="lawn size (sq. ft)"
              name="lawnSize"
              component={InputField}
              rows={4}
            />
            <Tooltip
              className={styles.lawnSizeModalTooltip}
              content={
                <div className={styles.tooltipContent}>
                  <p>Lawn size guide</p>
                  <ul>
                    <li>Two-car garage is ~500 square feet</li>
                    <li>Tennis court is ~2,000 square feet</li>
                    <li>1/4 acre is ~10,000 square feet</li>
                    <li>1/2 acre is ~20,000 square feet</li>
                  </ul>
                </div>
              }
              zIndex={2147483650}
            />
            <Button type="submit" fullWidth isLoading={isSubmitting}>
              Submit
            </Button>
          </>
        )}
      </Form>

      <div className={styles.imageWrapper}>
        <img
          alt="Applying fertilizer"
          height={211}
          width={335}
          src={imgSrc}
          srcSet={imgSrcSet}
        />
      </div>
    </div>
  );
};

export default LawnSizeModal;
